// Cliente HTTP fino para as Edge Functions do Supabase — sem SDK (@supabase/supabase-js não é
// necessário: este app nunca acessa tabela/Storage/Realtime
// diretamente, só Edge Functions). fetch puro evita dependência não justificada.
//
// Todo request ao gateway de Edge Functions do Supabase precisa do header `apikey` (a publishable
// key do projeto) — isso é exigência do gateway, não acesso a dado (supabase/config.toml confirma
// que só auth-login roda com verify_jwt=false; as demais exigem um JWT válido em Authorization,
// verificado pelo próprio gateway antes mesmo de chegar na função).

const FUNCTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL.replace(/\/$/, "")}/functions/v1`;
const PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
  };
}

/** Erro de resposta HTTP da API (4xx/5xx) — sempre carrega o `code` devolvido pela Edge Function. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

/** Falha de rede (sem resposta do servidor) — distinta de ApiError para permitir tela de "sem conexão". */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("Falha de conexão com o servidor.");
    this.name = "NetworkError";
    this.cause = cause;
  }
}

interface CallOptions {
  /** access_token da sessão do associado — omitido nas chamadas antes do login (ex.: auth-login). */
  accessToken?: string;
}

export async function callFunction<TBody extends object, TResponse>(
  name: string,
  body: TBody,
  options: CallOptions = {},
  query: Record<string, string | number | null | undefined> = {},
): Promise<TResponse> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== null && value !== undefined && value !== "") params.set(key, String(value));
  }
  const suffix = params.size > 0 ? `?${params.toString()}` : "";
  let response: Response;
  try {
    response = await fetch(`${FUNCTIONS_URL}/${name}${suffix}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: PUBLISHABLE_KEY,
        // Sem sessão ainda (ex.: login), manda a publishable key — mesmo padrão do SDK oficial do
        // Supabase para chamadas não autenticadas de Edge Function.
        Authorization: `Bearer ${options.accessToken ?? PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify(body),
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch {
    json = null;
  }

  if (!response.ok) {
    const errorBody = json as Partial<ApiErrorBody> | null;
    const code = errorBody?.error?.code ?? "unknown_error";
    const message = errorBody?.error?.message ?? "Erro inesperado ao falar com o servidor.";
    throw new ApiError(response.status, code, message);
  }

  return json as TResponse;
}

/** Chamada PATCH autenticada para contratos de escrita controlada. */
export async function callFunctionPatch<TBody extends object, TResponse>(
  name: string,
  query: Record<string, string | number | null | undefined>,
  body: TBody,
  options: CallOptions = {},
): Promise<TResponse> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== null && value !== undefined && value !== "") params.set(key, String(value));
  }
  const suffix = params.size > 0 ? `?${params.toString()}` : "";
  let response: Response;
  try {
    response = await fetch(`${FUNCTIONS_URL}/${name}${suffix}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        apikey: PUBLISHABLE_KEY,
        Authorization: `Bearer ${options.accessToken ?? PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify(body),
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch {
    json = null;
  }

  if (!response.ok) {
    const errorBody = json as Partial<ApiErrorBody> | null;
    throw new ApiError(
      response.status,
      errorBody?.error?.code ?? "unknown_error",
      errorBody?.error?.message ?? "Erro inesperado ao falar com o servidor.",
    );
  }

  return json as TResponse;
}

/** Chamada DELETE autenticada para contratos que revogam estado controlado. */
export async function callFunctionDelete<TBody extends object, TResponse>(
  name: string,
  body: TBody,
  options: CallOptions = {},
): Promise<TResponse> {
  let response: Response;
  try {
    response = await fetch(`${FUNCTIONS_URL}/${name}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        apikey: PUBLISHABLE_KEY,
        Authorization: `Bearer ${options.accessToken ?? PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify(body),
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch {
    json = null;
  }

  if (!response.ok) {
    const errorBody = json as Partial<ApiErrorBody> | null;
    throw new ApiError(
      response.status,
      errorBody?.error?.code ?? "unknown_error",
      errorBody?.error?.message ?? "Erro inesperado ao falar com o servidor.",
    );
  }

  return json as TResponse;
}

/** Chamada autenticada de leitura para Edge Functions. Mantém o fluxo POST de login inalterado. */
export async function callFunctionGet<TResponse>(
  name: string,
  query: Record<string, string | number | null | undefined> = {},
  options: CallOptions = {},
): Promise<TResponse> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== null && value !== undefined && value !== "") {
      params.set(key, String(value));
    }
  }

  const suffix = params.size > 0 ? `?${params.toString()}` : "";
  let response: Response;
  try {
    response = await fetch(`${FUNCTIONS_URL}/${name}${suffix}`, {
      method: "GET",
      headers: {
        apikey: PUBLISHABLE_KEY,
        Authorization: `Bearer ${options.accessToken ?? PUBLISHABLE_KEY}`,
      },
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch {
    json = null;
  }

  if (!response.ok) {
    const errorBody = json as Partial<ApiErrorBody> | null;
    throw new ApiError(
      response.status,
      errorBody?.error?.code ?? "unknown_error",
      errorBody?.error?.message ?? "Erro inesperado ao falar com o servidor.",
    );
  }

  return json as TResponse;
}
